import type { ComponentProps } from 'react'
import { Link } from 'react-router'
import { useHitTarget } from '../responsive/hitTarget'
import './inlineLink.css'

type Props = ComponentProps<typeof Link>

/**
 * A standalone text link with a real hit target — #169.
 *
 * Five of these ship: SignIn's "Forgot your password?" and "Create an
 * account", ForgotPassword's "Sign in" and "Back to sign in", and SignUp's
 * "Sign in". Each was a bare `<Link>` measuring 134 × **15px**, against a
 * documented 24px inline target that rises to 44px below 640px.
 *
 * **Why this is a component and not a class.** The 44px lift is a JS hook —
 * CLAUDE.md's single breakpoint source — so a class alone cannot express it,
 * and the two sites #169 found had already drifted apart with nothing to hold
 * them together. The issue's own count was two; there were six. A shared
 * component plus the reuse guard in the test file is what makes that countable
 * rather than re-counted by hand each time.
 *
 * **`display: inline-flex` in the CSS is load-bearing, not cosmetic.**
 * `min-height` has no effect at all on a non-replaced inline element, and `<a>`
 * is inline by default. Writing the height here and leaving the element inline
 * produces a component whose every unit test passes and whose rendered box is
 * still 15px. That is the exact failure CLAUDE.md describes — a check green for
 * the wrong reason — so the display mode is asserted alongside the height.
 *
 * **Not for a link inside a sentence.** CLAUDE.md carries one narrow exemption,
 * matching WCAG 2.5.8: a link whose position is determined by the flow of
 * surrounding prose keeps its natural line box, because padding it would break
 * the paragraph it lives in. `src/App.tsx`'s 404 is the only such site, and the
 * reuse guard pins it as the only one.
 */
export function InlineLink({ className, style, ...rest }: Props) {
  const target = useHitTarget('inline')
  return (
    <Link
      className={['inline-link', 'hoverable', className].filter(Boolean).join(' ')}
      style={{
        ...style,
        // After `style`, as `Button`'s min-height is and for the same reason: a
        // caller theming the link must not be able to shrink it under the
        // documented target by accident.
        //
        // **Both axes**, and the second one was missing until the hit-target
        // sweep measured it: SignUp's "Sign in" is a short label, and at 320px
        // it was 38.7 x 44 — the floor met on the axis that was thought about
        // and missed on the axis that was not. The same mistake the avatar's
        // comment records avoiding, made here anyway. A target is a box, and
        // M0-R29 does not name an axis.
        minHeight: target,
        minWidth: target,
      }}
      {...rest}
    />
  )
}
